import { Box, TextField as ODSTextField } from "@origin-digital/ods-core";
import styled, { css } from "styled-components";
import { IconButton, Paper, Popper } from "@mui/material";

export const HighlightMark = styled("mark")`
  background-color: ${({ theme }) => theme.palette.backgroundColor.info.light};
  color: ${({ theme }) => theme.palette.text.primary};
`;

const ICON_BUTTON_SIZE = 44;

export const StyledTextField = styled(ODSTextField)`
  width: 100%;

  .MuiInputBase-root {
    min-height: ${({ theme }) => theme.space.xxxlarge + theme.space.xsmall}px;
    padding-top: ${({ theme }) => theme.space.small}px !important;
    padding-bottom: ${({ theme }) => theme.space.small}px !important;
    padding-left: ${({ theme }) => theme.space.small}px !important;
    padding-right: ${({ theme }) =>
      theme.space.xsmall +
      ICON_BUTTON_SIZE +
      theme.space.xsmall +
      ICON_BUTTON_SIZE +
      theme.space.small}px !important;
    background-color: ${({ theme }) => theme.palette.background.paper};
    border-radius: ${({ theme }) =>
      theme.shape.borderRadii.xlarge}px !important;
    position: relative;
    border: 0;
    box-shadow: 0 6px 22px
      ${({ theme }) => theme.palette.boxShadow.color.primary};
    z-index: 1;
  }

  .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiInputBase-root:hover .MuiOutlinedInput-notchedOutline,
  .MuiInputBase-root.Mui-focused .MuiOutlinedInput-notchedOutline {
    border: none;
  }

  .MuiInputBase-input {
    padding: 0 !important;
    font-size: ${({ theme }) => theme.typography.subtitle1.fontSize};
    line-height: ${({ theme }) => theme.space.xxxlarge}px;
  }

  .MuiInputBase-input::placeholder {
    color: ${({ theme }) => theme.colors.neutralLight};
    opacity: 1;
  }
`;

export const StyledSearchIconContainer = styled("span")`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0 ${({ theme }) => theme.space.small}px 0
    ${({ theme }) => theme.space.xsmall}px;
`;

export const SparkleAdornment = styled("span")`
  display: inline-flex;
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.neutralLight};

  svg {
    width: 100%;
    height: 100%;
  }
`;

export const SearchActions = styled("span")`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xsmall}px;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    gap: ${({ theme }) => theme.space.xxsmall}px;
  }
`;

export const ExternalSearchActions = styled("div")`
  position: absolute;
  top: 0;
  right: ${({ theme }) => theme.space.xsmall}px;
  height: ${({ theme }) => theme.space.xxxlarge + theme.space.xsmall}px;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.xsmall}px;
  z-index: 2;

  ${({ theme }) => theme.breakpoints.down("sm")} {
    gap: ${({ theme }) => theme.space.xxsmall}px;
  }
`;

export const SearchSubmitButton = styled(IconButton)`
  && {
    width: 44px;
    height: 44px;
    border-radius: 100%;
    background: ${({ theme }) => theme.colors.secondary};
    color: ${({ theme }) => theme.colors.neutralDark};
  }

  &&:hover {
    background: ${({ theme }) => theme.colors.secondary};
    filter: brightness(0.98);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const SearchContainer = styled("div")`
  position: relative;
  width: 100%;
`;

export const Container = styled("div").withConfig({
  shouldForwardProp: (prop) => !["showGlow", "isOpen"].includes(String(prop)),
})<{ showGlow: boolean; isOpen: boolean }>`
  z-index: 11;
  position: relative;
  width: 100%;

  ${({ isOpen }) =>
    isOpen &&
    css`
      .MuiInputBase-root {
        box-shadow: none !important;
      }
    `}

  ${({ showGlow, theme }) =>
    showGlow &&
    css`
      /* Oversized, offset glow to softly frame the search field without covering it. */
      &::before {
        content: "";
        position: absolute;
        left: 1%;
        right: 1%;
        top: 8%;
        bottom: -26%;
        z-index: -1;
        background: linear-gradient(
          90deg,
          ${theme.colors.secondary} 0%,
          ${theme.colors.secondary} 100%
        );
        opacity: 0.3;
        filter: blur(100px);
        pointer-events: none;
      }

      ${theme.breakpoints.down("sm")} {
        &::before {
          left: 0;
          right: 0;
          top: 12%;
          bottom: -22%;
          filter: blur(22px);
        }
      }
    `}
`;

export const StyledCancelIconButton = styled(IconButton).withConfig({
  shouldForwardProp: (prop) => String(prop) !== "open",
})<{ open: boolean }>`
  transition: 0.2s ease;
  pointer-events: ${({ open }) => (open ? "initial" : "none")};
  opacity: ${({ open }) => (open ? 1 : 0)};
`;

export const StyledPopper = styled(Popper)`
  &.MuiAutocomplete-popper {
    z-index: 0;
  }
`;

export const StyledPaper = styled(Paper)`
  border-radius: ${({ theme }) => theme.shape.borderRadii.xlarge}px !important;
  padding-top: ${({ theme }) => theme.space.xxxlarge + theme.space.xsmall}px;
  padding-right: ${({ theme }) => theme.space.medium}px;
  padding-bottom: ${({ theme }) => theme.space.medium}px;
  padding-left: ${({ theme }) => theme.space.medium}px;
  box-shadow: 0 6px 22px ${({ theme }) => theme.palette.boxShadow.color.primary} !important;

  .MuiAutocomplete-listbox {
    max-height: 100%;
    padding-top: 0;
    padding-bottom: 0;
  }
  .MuiAutocomplete-option {
    min-height: ${({ theme }) => theme.space.xxlarge}px;
    padding: ${({ theme }) => theme.space.small}px;
    position: relative;

    &::after {
      content: "";
      position: absolute;
      bottom: 0;
      left: ${({ theme }) => theme.space.large}px;
      right: ${({ theme }) => theme.space.large}px;
      height: 1px;
      background-color: ${({ theme }) => theme.colors.disabled};
    }

    &:hover,
    &.Mui-focused,
    &[aria-selected="true"],
    &[aria-selected="true"].Mui-focused {
      background-color: transparent !important;
    }
  }

  /* Below lg, the FullPageCoverContainer paints a 100vh white panel behind the
     dropdown to give mobile search a takeover feel. With short dropdown
     content (SearchEmptyState, NoSuggestions, SearchingSkeleton) the cover
     extended visibly below the bordered Paper. Grow the Paper to roughly
     match the cover and flatten the bottom corners so it bleeds into the
     viewport edge. Approximate offset (input height + Popper 8px offset +
     SearchSection padding); pixel-accurate alignment would need JS to read
     the Popper position. */
  ${({ theme }) => theme.breakpoints.down("lg")} {
    min-height: calc(100vh - 120px);
    border-bottom-left-radius: 0 !important;
    border-bottom-right-radius: 0 !important;
  }
`;

export const SuggestionHeading = styled("p")`
  margin: 0;
  padding-left: ${({ theme }) => theme.space.large}px;
  font-size: ${({ theme }) => theme.typography.body2.fontSize};
  color: ${({ theme }) => theme.colors.neutralDark};
`;

export const FullPageCoverContainer = styled("div").withConfig({
  shouldForwardProp: (prop) => String(prop) !== "show",
})<{ show: boolean }>`
  ${({ theme }) => theme.breakpoints.down("lg")} {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100vh;
    background-color: ${({ theme }) => theme.palette.background.paper};
    z-index: 10;

    transition: opacity 0.2s ease-in-out;
    opacity: ${({ show }) => (show ? 1 : 0)};
    pointer-events: ${({ show }) => (show ? "initial" : "none")};
  }
`;

export const SearchSectionWrapper = styled("div")`
  position: relative;
`;

export const BackgroundWrapper = styled(Box)`
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
  border-radius: ${({ theme }) => theme.shape.borderRadii.large}px;
  svg {
    width: 100%;
    height: 100%;
  }
`;

export const SearchContent = styled("div")`
  position: relative;
  z-index: 1;
`;

// Visually-hidden container for an accessibility live region.
// Stays mounted so screen readers reliably announce text-content changes.
export const VisuallyHidden = styled("div")`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
`;
